import { userHasPermission } from '@/utils/localStorageManager'
import { clinicHasFeature, professionalHasFeature } from '@/utils/featureGating'
/* ---------------- user menu -------------- */

export const showDigitalSignature = () => {
  const clinic = JSON.parse(localStorage.getItem('clinic'))
  return clinic && ['DOCTOR_MANAGER', 'DOCTOR'].includes(clinic.role)
}

export const showPersonalData = () => {
  const clinic = JSON.parse(localStorage.getItem('clinic'))
  return clinic && ['MANAGER', 'DOCTOR_MANAGER', 'DOCTOR'].includes(clinic.role)
}

export const showAttendanceConfig = () => {
  const clinic = JSON.parse(localStorage.getItem('clinic'))
  return clinic && ['DOCTOR_MANAGER', 'DOCTOR'].includes(clinic.role)
}

/* ---------- side bar ------------- */

export const showDoctorOverview = () => {
  const clinic = JSON.parse(localStorage.getItem('clinic'))
  return clinic && ['DOCTOR_MANAGER', 'DOCTOR'].includes(clinic.role)
}

export const showAcademy = () => {
  const clinic = JSON.parse(localStorage.getItem('clinic'))
  // remove clinicId validation to show this features for all doctors
  return clinic
}

export const showTaxInvoices = () => clinicHasFeature({ name: 'Notas Fiscais' })

export const showInvoicingTiss = () =>
  clinicHasFeature({ name: 'Faturamento TISS' })

export const showInvoicingSus = () =>
  clinicHasFeature({ name: 'Faturamento SUS' })

export const showCallPanel = () =>
  clinicHasFeature({ name: 'Painel de chamadas' })

export const canForwardPatient = () => {
  const clinic = JSON.parse(localStorage.getItem('clinic'))
  return clinic?.call_panel_setting?.can_forwarding === true
}

export const showPublicSchedule = () =>
  clinicHasFeature({ name: 'Agendamento online' })

export const showOnlending = () => clinicHasFeature({ name: 'Repasse medico' })

// "Minha Produção": relatório de produção travado no próprio profissional.
// Três condições: a clínica tem o módulo de repasse, a pessoa tem a permissão
// RepMedMinProd1, e o perfil é médico.
//
// A permissão é o que permite REVOGAR o acesso de um médico específico. O papel
// continua exigido porque a tela mostra a produção do próprio profissional
// logado — o backend impõe o professional_id do usuário e recusa quem não tem
// profissional vinculado, então liberar para uma secretária não teria efeito útil.
// Único ponto de gate no front: o menu (FeatureGroup) e a tela (MyProduction.vue)
// consomem esta mesma função.
export const showMyProduction = () => {
  const clinic = JSON.parse(localStorage.getItem('clinic'))
  return (
    showOnlending() &&
    userHasPermission('RepMedMinProd1') &&
    clinic &&
    ['DOCTOR_MANAGER', 'DOCTOR'].includes(clinic.role)
  )
}

// Gating de feature POR CLÍNICA. Delega ao módulo compartilhado
// (@/utils/featureGating), que é a fonte única da regra e o mesmo consumido pelos
// react-apps — antes da spec 15 esta lógica estava reimplementada em 4 lugares.
// Paridade preservada: `rollout_mode` é ignorado aqui de propósito, porque a
// pergunta é sobre a CLÍNICA. Para gate por PESSOA use showFeatureForProfessional.
export const showFeature = feature => clinicHasFeature({ name: feature })

export const showFeatureByKey = key => clinicHasFeature({ key })

// Gating de feature POR PROFISSIONAL logado: aplica `clinic_features.rollout_mode`
// (all | allowlist | denylist). É o gate correto quando a feature libera algo para
// o médico, não para a clínica — ex. DAAI, e o opt-in da Consulta 3.0.
export const showFeatureForProfessional = feature =>
  professionalHasFeature({ name: feature })

export const showFeatureForProfessionalByKey = key =>
  professionalHasFeature({ key })

// Feature flag de coexistência CC v1 ↔ v2 (ADR-006). Gating POR CLÍNICA/UNIDADE
// logada — lê clinic_features (NÃO usar o featureIsActive global p/ isto).
export const showSurgeryCenterV2 = () => showFeatureByKey('centro-cirurgico-v2')

// Consulta 3.0 — a partir da remoção do chaveamento, é a ÚNICA consulta do
// sistema: não há mais gate (`showAttendanceV3Shell` + feature `attendance-v3`
// no front). A feature segue cadastrada no backend por ora, apenas ignorada
// pelo front, para permitir rollback revertendo só este PR.

// Documentos e exames 3.0 — a aba React é o único caminho: o gate do front
// (`showDocsV3React`) foi removido junto com o da consulta.
//
// ATENÇÃO (operacional): a feature `documentos-exames-v3` continua existindo no
// BACKEND e comanda a FONTE DE LEITURA do attendance_file por clínica
// (AttendanceReadSource). Clínica sem a feature ligada lê do LEGADO — com a UI
// nova sempre ativa, isso significa UI nova sobre dados legados. Pré-requisito
// para este estado: a feature ligada em todas as clínicas (e a reconciliação
// `ReconcileAttendanceFilesCommand` executada antes de cada ativação).

// DAAI (Doctor Assistant) — sempre foi gating POR PROFISSIONAL, mas a regra estava
// copiada à mão em 3 componentes (`hasFeature && professionals.some(...)`), com
// lista vazia negando para todos. Isso agora é o modo `allowlist`, que o backfill
// da spec 15 aplicou a todas as linhas desta feature.
export const showDoctorAssistant = () =>
  showFeatureForProfessional('Resultado Doctor Assistant')

// Módulo Compras — pedidos de compra, fornecedores e integração com
// estoque/financeiro (novo módulo React, do zero). Gating POR CLÍNICA via
// clinic_features (key `compras`), habilitado no aquilae-backoffice.
export const showCompras = () => showFeatureByKey('compras')

export const showFinancial = () => {
  const perm = ['FiConRec1', 'FiConRec3', 'FiConRec4', 'FiConRec5']
  const hasPermition = perm.find(key => userHasPermission(key))
  return hasPermition
}

export const showFinancialButton = () => {
  const perm = ['FiConRec2', 'FiConRec6', 'GerPenPen1']
  const hasPermition = perm.find(key => userHasPermission(key))
  return hasPermition
}

export const showFinancialButtonSidebar = () => {
  const perm = [
    'FiConFlag2',
    'FiConFlag1',
    'FiConFlag5',
    'FiConFlag4',
    'FiConFlag3',
    'FiConCat3',
    'FiConCat5',
    'FiConCat1',
    'FiConCat4',
    'FiConCat2',
    'FiConCoBan5',
    'FiConCoBan3',
    'FiConCoBan1',
    'FiConCoBan2',
    'FiConCoBan4',
    'FiConMaq1',
    'FiConMaq4',
    'FiConMaq3',
    'FiConMaq5',
    'FiConMaq2',
    'FiConPref1',
    'FiConPref2',
    'FiConMetPag2',
    'FiConMetPag5',
    'FiConMetPag1',
    'FiConMetPag4',
    'FiConMetPag3',
    'FiConPag4',
    'FiConPag2',
    'FiConPag3',
    'FiConPag5',
    'FiConPag1',
    'FiConRec5',
    'FiConRec3',
    'FiConRec4',
    'FiConRec2',
    'FiConRec1',
    'FiConPlan3',
    'FiConPlan1',
    'FiConPlan2',
    'FiConPlan4',
    'FiDash1',
    'FiFlu1',
    'FiMov3',
    'FiMov1',
    'FiMov2'
  ]
  const hasPermition = perm.find(key => userHasPermission(key))
  return hasPermition
}

export const canCreateAppointmentTypeFitting = () => {
  return userHasPermission('FpAgen9')
}

export const hasPermissionPatientCreditCategory = () => {
  return userHasPermission('FiConCred2')
}

export const hasPermissionLabpacsExams = () => {
  return userHasPermission('LaudoMedLab1')
}

export const showPersonalManager = () => {
  const clinic = JSON.parse(localStorage.getItem('clinic'))
  return clinic && ['MANAGER', 'DOCTOR_MANAGER'].includes(clinic.role)
}

// Gate generico para acoes restritas a perfis de gestor (MANAGER,
// DOCTOR_MANAGER e quaisquer *_MANAGER). Usado para liberar a reversao de lotes.
export const isManagerRole = () => {
  const clinic = JSON.parse(localStorage.getItem('clinic'))
  return !!clinic?.role?.toUpperCase().includes('MANAGER')
}

export const isUserAdmin = () => {
  const user = JSON.parse(localStorage.getItem('user'))
  return user.type && ['MANAGER', 'DOCTOR_MANAGER'].includes(user.type)
}

export const showFaturamentoTISS = () => {
  const perm = [
    'FatTissLotAEnv1',
    'FatTissFat1',
    'FatTissFat3',
    'FatTissFat6',
    'FatTissConf1',
    'FatTissLotEnv1',
    'FatTissLotEnv3',
    'FatTissLotEnv2',
    'FatTissDash1',
    'FatTissRelGui2',
    'FatTissFat2',
    'FatTissRelGui1',
    'FatTissLotAEnv2',
    'FatTissLotFin1',
    'FatTissLotAEnv4',
    'FatTissMatMed1',
    'FatTissMatMed3',
    'FatTissMatMed2',
    'FatTissFat4'
  ]
  const hasPermition = perm.find(key => userHasPermission(key))
  return showInvoicingTiss() && hasPermition
}

export const showFaturamentoSUS = () => {
  const perm = [
    'FatSusRelAIH',
    'FatSusLotFin',
    'FatSusLotAEnv',
    'FatSusRelAPAC',
    'FatSusRelBPA',
    'FatSusLotEnv',
    'FatSusConf',
    'FatSusDash'
  ]
  const hasPermition = perm.find(key => userHasPermission(key))
  return hasPermition
}
